import { useState, useEffect } from "react";
import { WorkflowData } from "../WorkflowEditor";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { FileText, Plus, Trash2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";

interface DocumentsTabProps {
  data: WorkflowData;
  onChange: (data: WorkflowData) => void;
}

interface DocumentTemplate {
  id: string;
  template_name: string;
  template_category: string;
}

export function DocumentsTab({ data, onChange }: DocumentsTabProps) {
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selectedPhase, setSelectedPhase] = useState("");
  const [isRequired, setIsRequired] = useState(true);

  useEffect(() => {
    loadTemplates();
  }, []);

  async function loadTemplates() {
    try {
      const { data: templatesData, error } = await supabase
        .from('document_template_library')
        .select('id, template_name, template_category')
        .eq('is_active', true)
        .order('template_name');

      if (error) throw error;
      setTemplates(templatesData || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  }

  function addDocument() {
    if (!selectedTemplate || !selectedPhase) return;

    const phaseIndex = parseInt(selectedPhase);
    const newPhases = [...data.phases];
    const phase = newPhases[phaseIndex];
    
    if (!phase.required_documents) {
      phase.required_documents = [];
    }

    phase.required_documents.push({
      template_id: selectedTemplate,
      required: isRequired,
    });

    onChange({ ...data, phases: newPhases });
    setSelectedTemplate("");
    setSelectedPhase("");
  }

  function removeDocument(phaseIndex: number, docIndex: number) {
    const newPhases = [...data.phases];
    newPhases[phaseIndex].required_documents.splice(docIndex, 1);
    onChange({ ...data, phases: newPhases });
  }

  function getTemplateName(templateId: string) {
    const template = templates.find(t => t.id === templateId);
    return template?.template_name || "Unknown Template";
  }

  if (loading) {
    return <div className="p-6">Loading templates...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Document Templates</h2>
        <p className="text-sm text-muted-foreground">
          Link document templates to workflow phases
        </p>
      </div>

      {/* Add Document Form */}
      <Card className="p-4">
        <div className="space-y-4">
          <h3 className="font-medium">Add Document to Phase</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Select Phase</Label>
              <Select value={selectedPhase} onValueChange={setSelectedPhase}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose phase..." />
                </SelectTrigger>
                <SelectContent>
                  {data.phases.map((phase, index) => (
                    <SelectItem key={phase.id} value={index.toString()}>
                      Phase {index + 1}: {phase.name || "Untitled"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Select Template</Label>
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(template => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.template_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Required?</Label>
              <Select value={isRequired.toString()} onValueChange={(v) => setIsRequired(v === 'true')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Required</SelectItem>
                  <SelectItem value="false">Optional</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={addDocument} disabled={!selectedTemplate || !selectedPhase}>
            <Plus className="h-4 w-4 mr-2" />
            Add Document
          </Button>
        </div>
      </Card>

      {/* Documents by Phase */}
      <div className="space-y-4">
        {data.phases.map((phase, phaseIndex) => (
          <Card key={phase.id} className="p-4">
            <h3 className="font-medium mb-3">
              Phase {phaseIndex + 1}: {phase.name || "Untitled Phase"}
            </h3>
            {(!phase.required_documents || phase.required_documents.length === 0) ? (
              <p className="text-sm text-muted-foreground">No documents linked</p>
            ) : (
              <div className="space-y-2">
                {phase.required_documents.map((doc: any, docIndex: number) => (
                  <div key={docIndex} className="flex items-center justify-between p-2 border rounded">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      <span className="text-sm">{getTemplateName(doc.template_id)}</span>
                      <Badge variant={doc.required ? "default" : "secondary"} className="text-xs">
                        {doc.required ? "Required" : "Optional"}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeDocument(phaseIndex, docIndex)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}